import { Menu, Moon, PanelLeft, Sun, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { applyThemePreference, getPreferences, getResolvedTheme, savePreferences, THEME_EVENT } from '../lib/preferences';
import { AccountMenu } from './AccountMenu';
import { Logo } from './Logo';

type NavItem = { to: string; label: string; icon?: LucideIcon; end?: boolean };

const navItems: NavItem[] = [
  { to: '/', label: 'Home', end: true },
  { to: '/tools', label: 'Tools' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/guides', label: 'Guides' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' }
];

export function Header({ toolsOpen, onToggleTools }: { toolsOpen: boolean; onToggleTools: () => void }) {
  const [open, setOpen] = useState(false);
  const [resolvedTheme, setResolvedTheme] = useState<'dark' | 'light'>(() => getResolvedTheme());
  const { user } = useAuth();

  useEffect(() => {
    applyThemePreference();
    const media = window.matchMedia('(prefers-color-scheme: light)');
    const sync = () => setResolvedTheme(getResolvedTheme());
    const onThemeChange = () => sync();
    media.addEventListener('change', sync);
    window.addEventListener(THEME_EVENT, onThemeChange);
    return () => {
      media.removeEventListener('change', sync);
      window.removeEventListener(THEME_EVENT, onThemeChange);
    };
  }, []);

  function toggleTheme() {
    const preferences = getPreferences();
    const nextTheme = resolvedTheme === 'dark' ? 'light' : 'dark';
    savePreferences({ ...preferences, theme: nextTheme });
    setResolvedTheme(nextTheme);
  }

  return (
    <header className="site-header site-header-wide">
      <div className="header-inner header-inner-wide">
        <div className="header-leading">
          <button
            className="icon-button header-tools-button"
            type="button"
            onClick={() => {
              setOpen(false);
              onToggleTools();
            }}
            aria-label={toolsOpen ? 'Close image tools menu' : 'Open image tools menu'}
            aria-expanded={toolsOpen}
          >
            {toolsOpen ? <X size={20} /> : <PanelLeft size={20} />}
          </button>
          <Logo />
        </div>

        <nav className="desktop-nav" aria-label="Primary navigation">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => (isActive ? 'active' : '')}>
              {item.icon && <item.icon size={16} />}
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="header-actions">
          <button className="icon-button" onClick={toggleTheme} aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} theme`}>
            {resolvedTheme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          {user ? <AccountMenu /> : <Link className="button button-primary header-signin" to="/auth">Sign in / Sign up</Link>}
          <button
            className="icon-button mobile-menu-button"
            onClick={() => {
              if (toolsOpen) onToggleTools();
              setOpen((value) => !value);
            }}
            aria-label={open ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={open}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="mobile-nav">
          <div className="shell">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} onClick={() => setOpen(false)}>
                {item.icon && <item.icon size={17} />}
                {item.label}
              </NavLink>
            ))}
            <Link to={user ? '/settings' : '/auth'} onClick={() => setOpen(false)}>
              {user ? 'Account settings' : 'Sign in / Sign up'}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
